import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { AppleSignInButton } from './AppleSignInButton'
import { GoogleSignInButton } from './GoogleSignInButton'

type SocialProvider = 'google' | 'apple'

interface ISocialLoginButtonsProps {
  /** Forwarded to both providers so success routing can honor `/login?redirect=...`, matching LoginComponent. */
  redirectTo?: string
  /** Disables both providers, e.g. while the email/password form is submitting. */
  disabled?: boolean
  /** Reports whether either provider is currently mid-flow, so the parent can disable the rest of the page. */
  onLoadingChange?: (isLoading: boolean) => void
}

export const SocialLoginButtons = ({ redirectTo, disabled, onLoadingChange }: ISocialLoginButtonsProps) => {
  const [activeProvider, setActiveProvider] = useState<SocialProvider | null>(null)

  useEffect(() => {
    onLoadingChange?.(activeProvider !== null)
  }, [activeProvider, onLoadingChange])

  const handleGoogleLoadingChange = useCallback((isLoading: boolean) => {
    setActiveProvider((current) => (isLoading ? 'google' : current === 'google' ? null : current))
  }, [])

  const handleAppleLoadingChange = useCallback((isLoading: boolean) => {
    setActiveProvider((current) => (isLoading ? 'apple' : current === 'apple' ? null : current))
  }, [])

  return (
    <Container>
      <Divider>
        <span>or</span>
      </Divider>
      <div className="flex flex-column gap-3">
        <GoogleSignInButton
          redirectTo={redirectTo}
          disabled={disabled || activeProvider === 'apple'}
          onLoadingChange={handleGoogleLoadingChange}
        />
        <AppleSignInButton
          redirectTo={redirectTo}
          disabled={disabled || activeProvider === 'google'}
          onLoadingChange={handleAppleLoadingChange}
        />
      </div>
    </Container>
  )
}

const Container = styled.div`
  margin-bottom: 1.5rem;
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray_70};
  font-size: 14px;
  margin: 1rem 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray_40};
  }

  span {
    padding: 0 12px;
  }
`
